import { InlineDestinationCountries } from './DestinationCountries.js';
import { amazonSearch } from '../../../../packages/shared/src/amazon-search.js';
import { amazonStore } from '../../../../packages/shared/src/amazon-stores.js';
import { affiliateDisclosure, clickTimeAffiliates, earnsCommission, type ClickTimeAffiliates } from '../../../../packages/shared/src/affiliate-disclosure.js';
import { Link } from 'react-router-dom';
import type { Country, Offer } from '../types.js';
import { OfficialStoreBadge, VerificationBadge } from './VerificationBadge.js';
import { OfferPromotion } from './OfferPromotion.js';
import { DeleteRecord, EditActions, InlineEdit, type FieldSpec } from './EditLink.js';
import {
  hasRecordedPrice,
  recentlyChecked,
  PRICE_MAX_AGE_MS,
  DELIVERY_MAX_AGE_MS,
} from '../../../../packages/shared/src/buying.js';
const date = (v: string) =>
  new Date(v).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }) + ' UTC';
export const buyingLinkTitle = (offer: Pick<Offer, 'customTitle' | 'retailer'>) =>
  offer.customTitle?.trim() || offer.retailer.name;
export function BuyingLinks({
  offers,
  countryName,
  countries = [],
  selectedCountry = '',
  fragranceId = '',
  originalId = '',
  affiliate,
  fragrance,
  onSaved,
}: {
  offers: Offer[];
  fragrance?: { brand: { name: string } | null; name: string; concentration: string };
  countryName?: string;
  countries?: Country[];
  selectedCountry?: string;
  fragranceId?: string;
  originalId?: string;
  // Required: without the server's click-time settings a tagged link would render with no disclosure.
  affiliate: boolean | ClickTimeAffiliates;
  onSaved?: () => void;
}) {
  const searchUrl = fragrance
    ? amazonSearch(
        selectedCountry,
        fragrance.brand?.name ?? '',
        fragrance.name,
        fragrance.concentration,
      )
    : null;
  const brandStore = amazonStore(selectedCountry, fragrance?.brand?.name ?? '');
  // Buying links leave through the site's own redirect, which records the choice and applies any
  // affiliate tag. Only the record to look up travels in the link; the destination is rebuilt server-side.
  const trail = (path: string) => {
    const params = new URLSearchParams();
    if (selectedCountry) params.set('country', selectedCountry);
    if (originalId) params.set('from', originalId);
    return path + (params.size ? '?' + params : '');
  };
  const offerLink = (o: Offer) => trail('/go/offer/' + o.id);
  const amazonLink = (kind: 'search' | 'store', destination: string) =>
    fragranceId ? trail('/go/amazon-' + kind + '/' + fragranceId) : destination;
  const fields = buyingLinkFields(selectedCountry);
  const eligible = offers.filter(
    (o) =>
      selectedCountry &&
      (!o.country || o.country === selectedCountry) &&
      !(o.country === null && o.hideFromOtherBuyingLinks) &&
      o.deliveryStatus !== 'UNSUPPORTED',
  );
  const supported = (o: Offer) =>
    o.country === selectedCountry &&
    o.deliveryStatus === 'SUPPORTED' &&
    recentlyChecked(o.deliveryCheckedAt, DELIVERY_MAX_AGE_MS);
  const countryLinks = eligible.filter((o) => o.country === selectedCountry),
    candidates = eligible.filter((o) => o.country === null);
  const hasAmazonOffer = eligible.some((o) => o.amazon);
  const visibleBrandStore = hasAmazonOffer ? null : brandStore;
  // The disclosure sits directly under each link it describes, where a reader decides to click.
  const clickTime = clickTimeAffiliates(affiliate);
  const disclosure = (
    <p className="affiliate-note below-button">
      {affiliateDisclosure}{' '}
      <Link to="/about#affiliate">How we make money</Link>
    </p>
  );
  function renderOffer(o: Offer) {
    const title = buyingLinkTitle(o);
    const priced = hasRecordedPrice(o);
    const fresh = recentlyChecked(o.priceCheckedAt, PRICE_MAX_AGE_MS);
    return (
      <div className="offer" key={o.id}>
        <VerificationBadge status={o.verificationStatus} />{' '}
        {o.officialStore && <OfficialStoreBadge />}
        <a
          href={offerLink(o)}
          target="_blank"
          rel="sponsored nofollow noopener noreferrer"
          className="retailer-link buying-button"
        >
          {title}
        </a>
        {earnsCommission(o.url, clickTime, o.affiliate) && disclosure}
        <OfferPromotion offer={o} />
        <dl className="offer-facts">
          <div>
            <dt>Price</dt>
            <dd className={priced ? 'offer-amount' : 'offer-missing'}>
              {priced ? (
                new Intl.NumberFormat('en-GB', { style: 'currency', currency: o.currency! }).format(
                  Number(o.price),
                )
              ) : (
                <a className="buying-button buying-button-secondary" href={offerLink(o)} target="_blank" rel="sponsored nofollow noopener noreferrer">
                  {o.amazon ? 'Check price on Amazon' : 'Check current price'}
                </a>
              )}
              {priced && <span className="offer-currency">{o.currency}</span>}
            </dd>
          </div>
          <div>
            <dt>Bottle size</dt>
            <dd>{o.bottleSizeMl ? o.bottleSizeMl + ' ml' : 'Check retailer'}</dd>
          </div>
        </dl>
        {/* The delivery answer stays on the summary, so opening the panel adds the evidence behind it
            rather than revealing something a buyer needed to see. */}
        <details className="offer-details">
          <summary>
            {o.amazon
              ? 'Delivery on Amazon'
              : supported(o)
                ? 'Delivery to ' + countryName + ' supported'
                : 'Delivery to ' + countryName + ' not confirmed'}
          </summary>
          {priced && o.priceCheckedAt && (
            <p className="checked-date">
              {fresh && o.availability === 'IN_STOCK'
                ? 'In stock when checked. '
                : fresh && o.availability === 'OUT_OF_STOCK'
                  ? 'Out of stock when checked. '
                  : ''}
              Price checked {date(o.priceCheckedAt)}.
            </p>
          )}
          <p className="link-verification-note">
            {o.amazon
              ? 'Check delivery on Amazon.'
              : supported(o)
                ? 'Shipping policy supports delivery to ' + countryName + '.'
                : 'Delivery to ' + countryName + ' is not confirmed.'}{' '}
            Final availability, delivery charges and taxes are confirmed at checkout.
          </p>
          {supported(o) && o.shippingEvidenceUrl && (
            <a
              className="checked-date"
              href={o.shippingEvidenceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Delivery policy · checked {date(o.deliveryCheckedAt!)}
            </a>
          )}
        </details>
        <EditActions>
          <InlineEdit
            entity="offers"
            id={o.id}
            label="Edit link"
            fields={fields}
            onSaved={onSaved}
          />
          <DeleteRecord
            entity="offers"
            id={o.id}
            what={'the ' + title + ' buying link'}
            onDeleted={onSaved}
          />
        </EditActions>
      </div>
    );
  }
  return (
    <div className="buying-links">
      <h4>{countryName ? 'Buying in ' + countryName : 'Where to buy'}</h4>
      {!countryLinks.length && !searchUrl && (
        <p className="availability-note">
          {selectedCountry
            ? 'No buying links for this country yet.'
            : 'Choose your country to see buying options.'}
        </p>
      )}
      {countryLinks.map(renderOffer)}
      {visibleBrandStore ? (
        <div className="offer amazon-brand-store">
          <VerificationBadge status={visibleBrandStore.verified ? 'VERIFIED' : 'UNVERIFIED'} />
          <a
            className="retailer-link buying-button"
            href={amazonLink('store', visibleBrandStore.url)}
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
          >
            {visibleBrandStore.kind === 'BRAND_SEARCH'
              ? 'Browse ' + visibleBrandStore.brand + ' on Amazon'
              : 'Visit ' + visibleBrandStore.brand + '’s Amazon store'}
          </a>
          {earnsCommission(visibleBrandStore.url, clickTime) && disclosure}
          <p className="link-verification-note">
            {visibleBrandStore.kind === 'BRAND_SEARCH'
              ? 'Brand-filtered search results; availability varies by country.'
              : 'Browse the brand’s range.'}{' '}
            Check the individual product’s seller, bottle size and delivery on Amazon.
          </p>
        </div>
      ) : (
        searchUrl &&
        !hasAmazonOffer && (
          <div className="offer amazon-search">
            <VerificationBadge status="UNVERIFIED" />
            <a
              className="retailer-link buying-button"
              href={amazonLink('search', searchUrl)}
              target="_blank"
              rel="sponsored nofollow noopener noreferrer"
            >
              Search Amazon
            </a>
            {earnsCommission(searchUrl, clickTime) && disclosure}
            <p className="link-verification-note">
              Search results for this alternative. Check the exact fragrance, concentration, bottle
              size and delivery before buying.
            </p>
          </div>
        )
      )}
      {!!candidates.length && (
        <details className="candidate-offers">
          <summary>Other buying links ({candidates.length}) · check delivery</summary>
          {candidates.map(renderOffer)}
        </details>
      )}
      <EditActions>
        <InlineEdit
          entity="offers"
          icon="plus"
          label="Add a buying link"
          base={buyingLinkBase(fragranceId, selectedCountry)}
          fields={fields}
          onSaved={onSaved}
        />
      </EditActions>
    </div>
  );
}

export function buyingLinkBase(fragranceId: string, selectedCountry = '') {
  return {
    fragranceId,
    url: '',
    customTitle: null,
    country: null,
    countries: selectedCountry ? [selectedCountry] : [],
    price: null,
    currency: null,
    bottleSizeMl: null,
    promoCode: null,
    discountType: null,
    discountValue: null,
    lastCheckedAt: null,
    shippingEvidenceUrl: null,
    verificationStatus: 'UNVERIFIED',
    verificationNotes: '',
    officialStore: false,
    affiliate: false,
    hideFromOtherBuyingLinks: false,
    deliveryStatus: 'UNKNOWN',
    deliveryCheckedAt: null,
    priceCheckedAt: null,
    availability: 'UNKNOWN',
    status: 'PUBLISHED',
  };
}

export function buyingLinkFields(selectedCountry = ""): FieldSpec[] {
  return [
    { key: 'url', label: 'Product buying URL' },
    {
      key: 'customTitle',
      label: 'Custom link title (optional)',
      extra: () => <p className="field-help">Leave blank to use the retailer’s current name.</p>,
    },
    {key:'countries',label:'Destination countries',type:'custom',render:({value,onChange})=><InlineDestinationCountries value={value??(selectedCountry?[selectedCountry]:[])} onChange={onChange}/>},
    { key: 'officialStore', label: 'Official store of this fragrance’s brand', type: 'checkbox' },
    {
      key: 'verificationStatus',
      label: 'Product link verified',
      type: 'checkbox',
      on: 'VERIFIED',
      off: 'UNVERIFIED',
    },
    { key: 'affiliate', label: 'Affiliate link, we may earn a commission', type: 'checkbox' },
    {
      key: 'hideFromOtherBuyingLinks',
      label: 'Hide from “Other buying links” in other countries',
      type: 'checkbox',
      extra: () => <p className="field-help">This link remains visible in every destination selected above.</p>,
    },
    {
      key: 'deliveryStatus',
      label: 'Delivery to selected destinations',
      type: 'select',
      empty: null,
      options: [
        { value: 'UNKNOWN', label: 'Not confirmed' },
        { value: 'SUPPORTED', label: 'Supported by shipping policy' },
        { value: 'UNSUPPORTED', label: 'Not supported' },
      ],
    },
    { key: 'shippingEvidenceUrl', label: 'Shipping policy / product delivery evidence URL' },
    { key: 'deliveryCheckedAt', label: 'Delivery checked (optional)', type: 'datetime' },
    {
      key: 'currency',
      label: 'Checkout currency',
      type: 'select',
      options: Intl.supportedValuesOf('currency').map((value) => ({ value, label: value })),
      empty: 'Use destination currency',
    },
    { key: 'price', label: 'Listed price' },
    { key: 'priceCheckedAt', label: 'Price checked (optional)', type: 'datetime' },
    {
      key: 'availability',
      label: 'Stock when price checked',
      type: 'select',
      empty: null,
      options: [
        { value: 'UNKNOWN', label: 'Unknown' },
        { value: 'IN_STOCK', label: 'In stock' },
        { value: 'OUT_OF_STOCK', label: 'Out of stock' },
      ],
    },
    { key: 'bottleSizeMl', label: 'Bottle size in ml', type: 'number' },
    { key: 'discountType', label: 'Affiliate discount', type: 'select', empty: 'No discount code', options: [{ value: 'PERCENTAGE', label: 'Percentage off' }, { value: 'FIXED_AMOUNT', label: 'Flat amount off' }] },
    { key: 'promoCode', label: 'Discount code', when: draft => Boolean(draft.discountType) },
    { key: 'discountValue', label: draft => draft.discountType === 'PERCENTAGE' ? 'Percentage off' : 'Flat amount off', type: 'number', when: draft => Boolean(draft.discountType) },
  ];
}
